import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Zap } from 'lucide-react';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { useLang } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';

export default function Header() {
  const { t } = useLang();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/', key: 'home' },
    { to: '/about', key: 'about' },
    { to: '/ingredients', key: 'ingredients' },
    { to: '/flavours', key: 'flavours' },
    { to: '/reviews', key: 'reviews' },
    { to: '/faq', key: 'faq' },
    { to: '/managers', key: 'managers' },
  ];

  const navClass = ({ isActive }) =>
    `relative font-cond tracking-wide text-sm uppercase transition-colors ${isActive ? 'text-white' : 'text-white/70 hover:text-white'} after:content-[''] after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:bg-[#ff2d2d] after:transition-all ${isActive ? 'after:w-full' : 'after:w-0 hover:after:w-full'}`;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[80] transition-all duration-300 ${scrolled ? 'bg-black/70 backdrop-blur-xl border-b border-white/10 py-3' : 'bg-transparent py-5'}`}
      >
        <div className="max-w-[1400px] mx-auto px-5 flex items-center justify-between gap-4">
          <Logo />

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={navClass} end={l.to === '/'}>
                {t(l.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-4">
            <div className="hidden md:block"><LanguageSwitcher /></div>
            <button
              onClick={() => navigate('/flavours')}
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-[#ff2d2d] text-white font-cond tracking-wide text-sm hover:bg-[#ff2d2d] transition-colors"
            >
              {t('tryNow')} <Zap className="w-4 h-4" fill="currentColor" />
            </button>
            <button onClick={() => setOpen(true)} className="relative text-white/90 hover:text-white" aria-label="Cart">
              <ShoppingCart className="w-6 h-6" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#ff2d2d] text-[10px] flex items-center justify-center font-bold">{count}</span>
              )}
            </button>
            <button onClick={() => setMenu(true)} className="lg:hidden text-white" aria-label="Menu">
              <Menu className="w-7 h-7" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div className={`fixed inset-0 z-[85] lg:hidden transition-all duration-300 ${menu ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setMenu(false)} />
        <div className={`absolute top-0 right-0 h-full w-4/5 max-w-sm bg-[#0a0a0a] border-l border-white/10 p-6 flex flex-col gap-2 transition-transform duration-300 ${menu ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between mb-6">
            <Logo onClick={() => setMenu(false)} />
            <button onClick={() => setMenu(false)} className="text-white/70"><X className="w-7 h-7" /></button>
          </div>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              onClick={() => setMenu(false)}
              className={({ isActive }) => `py-3 border-b border-white/5 font-cond text-2xl tracking-wide ${isActive ? 'text-[#ff2d2d]' : 'text-white'}`}
            >
              {t(l.key)}
            </NavLink>
          ))}
          <div className="mt-6 flex items-center justify-between">
            <LanguageSwitcher />
            <button
              onClick={() => { setMenu(false); navigate('/flavours'); }}
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-lg bg-[#ff2d2d] text-white font-cond tracking-wide"
            >
              {t('tryNow')} <Zap className="w-4 h-4" fill="currentColor" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
